import axios from "axios";
import { Loading, Message } from "element-ui";
import Qs from "qs";
import Vue from "vue";
import { formatParams } from "./formatParams";
import i18n from "@/i18n";
import router from "@/router";
import store from "@/store";
import { uuid } from "@/utils";

// loading实例
let loadingInstance;
// 记录当前并发请求数
let requestCount = 0;

const getBaseUrl = () => {
	const NODE_ENV = process.env.VUE_APP_NODE_ENV;
	const defaultBaseUrl = process.env.VUE_APP_BASE_URL || "/api";

	if (NODE_ENV === "tmp") {
		const baseUrl = localStorage.getItem("BASE_URL");

		return baseUrl || defaultBaseUrl;
	}

	return defaultBaseUrl;
};

const timeout = +process.env.VUE_APP_TIMEOUT || 30000;
const baseURL = getBaseUrl();

const instance = axios.create({
	// 设置超时时间
	timeout,
	// 请求前缀
	baseURL
});

instance.interceptors.request.use(
	(config) => {
		const token = store.getters.token;

		requestCount++;

		// 请求头headers设置token
		config.headers.Authorization = token;
		// 设置请求头类型
		config.headers["X-Requested-With"] = "XMLHttpRequest"; // 请求头部新增token
		// 请求头携带语言类型
		config.headers["X-Lt-Language"] = localStorage.getItem("lang") || "zh";
		// 请求头设置唯一id
		config.headers.traceId = uuid();

		if (config.isBlob) {
			config["responseType"] = "blob";
		}

		if (config.showLoading && !loadingInstance) {
			loadingInstance = Loading.service({
				lock: true,
				text: "Loading",
				spinner: "el-icon-loading",
				color: "#2C68FF",
				background: "rgba(0, 0, 0, 0.2)"
			});
		}

		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

instance.interceptors.response.use(
	(response) => {
		requestCount--;

		// 请求数为0时说明并发请求结束，清除loading状态
		if (requestCount === 0) {
			loadingInstance && loadingInstance.close();

			loadingInstance = undefined;
		}

		const code = response.status;
		const config = response.config;

		if ((code >= 200 && code < 300) || code === 304) {
			const requestCode = response.data.code;

			if (requestCode === 40103 || requestCode === 401 || requestCode === 40106) {
				// 同时出现多个提示语时只提示第一个
				if (document.querySelectorAll(".el-message").length === 0) {
					Message.warning(response.data.msg || i18n.tc("loginExpired"));
					store.commit("user/LOGIN_OUT");
					router.push("/login");
				}

				return Promise.reject(response.data);
			} else if (requestCode !== 0 && requestCode !== 200 && requestCode !== 21006 && requestCode !== undefined) {
				if (config.showMessage) {
					Message({
						showClose: true,
						type: "error",
						message: response.data.msg || i18n.tc("parameterError")
					});
				}

				return Promise.reject(response.data);
			}

			return Promise.resolve(response.data);
		} else {
			return Promise.reject(response.data);
		}
	},
	(error) => {
		requestCount--;

		// 请求数为0时说明并发请求结束，清除loading状态
		if (requestCount === 0) {
			loadingInstance && loadingInstance.close();
		}

		if (error.response) {
			switch (error.response.status) {
				case 40103:
					// 返回40103 清除token信息并跳转到登陆页面
					store.commit("user/LOGIN_OUT");
					router.push("/login");
					break;
				case 404:
					Message.error(i18n.tc("requestNotExist"));
					break;
				case 401:
					if (document.querySelectorAll(".el-message").length === 0) {
						Message.error(i18n.tc("loginExpired"));

						setTimeout(() => {
							store.commit("user/LOGIN_OUT");
							router.push("/login");
						}, 1000);
					}

					break;
				default:
					Message.error(error.response.data.msg);
			}
		} else {
			// 请求超时或者网络有问题
			if (error.message.includes("timeout")) {
				Message.error(i18n.tc("networkAnomaly"));
			} else {
				Message.error(i18n.tc("networkError"));
			}
		}

		return Promise.reject(error);
	}
);

Vue.prototype.$http = instance;

/**
 * post 请求
 * @param {string} url 请求地址
 * @param {*} data 请求参数
 * @param {*} config 请求配置
 * @param {boolean} config.showLoading 是否显示loading
 * @param {boolean} config.showMessage 是否显示message
 * @param {boolean} config.isFormatParams 是否格式化参数(清除空值参数)
 * @param {boolean} config.isBlob 是否是blob请求
 * @param {boolean} config.isQS 是否使用qs序列化参数
 * @param ...args 其他axios配置
 * @returns
 */
const post = (url, data, config = {}) => {
	const defaultConfig = {
		showLoading: true,
		showMessage: true,
		isFormatParams: false,
		isQS: true,
		headers: {
			"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
		}
	};
	const newConfig = { ...defaultConfig, ...config };
	const newData = newConfig.isFormatParams ? formatParams(data) : data;

	return instance.post(url, newConfig.isQS ? Qs.stringify(newData) : newData, newConfig);
};

/**
 * get 请求
 * @param {string} url 请求地址
 * @param {*} params 请求参数
 * @param {*} config 请求配置
 * @param {boolean} config.showLoading 是否显示loading
 * @param {boolean} config.showMessage 是否显示message
 * @param {boolean} config.isFormatParams 是否格式化参数(清除空值参数)
 * @param {boolean} config.isBlob 是否是blob请求
 * @param {boolean} config.isQS 是否使用qs序列化参数
 * @param ...args 其他axios配置
 * @returns
 */
const get = (url, params, config = {}) => {
	const defaultConfig = { isQS: false, isFormatParams: true, showLoading: true };
	const newConfig = { ...defaultConfig, ...config };
	const { isQS, isFormatParams, ...rest } = newConfig;
	const newParams = isFormatParams ? formatParams(params) : params;

	return instance.get(url, { params: isQS ? Qs.stringify(newParams) : newParams, ...rest });
};

/**
 * put 请求
 * @param {string} url 请求地址
 * @param {*} data 请求参数
 * @param {*} config 请求配置
 * @param {boolean} config.showLoading 是否显示loading
 * @param {boolean} config.showMessage 是否显示message
 * @param {boolean} config.isFormatParams 是否格式化参数(清除空值参数)
 * @param {boolean} config.isBlob 是否是blob请求
 * @param ...args 其他axios配置
 * @returns
 */
const put = (url, data, config = {}) => {
	const defaultConfig = {
		showLoading: true,
		showMessage: true,
		isFormatParams: false
	};
	const newConfig = { ...defaultConfig, ...config };
	const newData = newConfig.isFormatParams ? formatParams(data) : data;

	return instance.put(url, newData, newConfig);
};

/**
 * delete 请求
 * @param {string} url 请求地址
 * @param {*} data 请求参数
 * @param {*} config 请求配置
 * @param {boolean} config.showLoading 是否显示loading
 * @param {boolean} config.showMessage 是否显示message
 * @param {boolean} config.isFormatParams 是否格式化参数(清除空值参数)
 * @param {boolean} config.isBlob 是否是blob请求
 * @param {boolean} config.isQS 是否使用qs序列化参数
 * @param ...args 其他axios配置
 * @returns
 */
const del = (url, data, config = {}) => {
	const defaultConfig = { isQS: false, showLoading: true, isFormatParams: true };
	const newConfig = { ...defaultConfig, ...config };
	const { isQS, ...rest } = newConfig;
	const newData = newConfig.isFormatParams ? formatParams(data) : data;

	return instance.delete(url, { data: isQS ? Qs.stringify(newData) : newData, ...rest });
};

export default {
	post,
	get,
	put,
	delete: del
};
